import { Box, Center, Skeleton } from 'native-base';
import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import {
    Stashboard,
    StashboardMedia,
    useSpringBigStore,
    useTheme,
} from '@springbig/shared';
import { Caption, H3 } from '~/shared/Typography';
import { SliderLinkCard } from './MessagesSliderLinkCard';
import isYesterday from 'dayjs/plugin/isYesterday';
import { useEffect } from 'react';
import { useAppNavigate } from '~/pages';
import Carousel from '~/shared/Carousel';
import VisibilitySensor from 'react-visibility-sensor';
import { Button } from '~/shared/Theme';

dayjs.extend(isYesterday);

export type MessagesCarouselProps = {
    data: Stashboard;
    hasMoreContent?: boolean;
};

const MessageCarousel = ({ data, hasMoreContent }: MessagesCarouselProps) => {

    const { highlight_text } = useTheme();

    const { walletId } = useSpringBigStore();
    const { navigate, params } = useAppNavigate();

    const [isVisible, setIsVisible] = useState(false);

    const handleMms = (stashboard: StashboardMedia[]) => {

        return stashboard;
    };

    const [stashboardMedia, setStashboardMedia] = useState<StashboardMedia[]>(handleMms(data?.stashboard_media));

    const handleDate = (date: string) => {

        const now = dayjs();
        const msgSentAt = dayjs(date);
        const today = now.format('MM/DD/YYYY');
        const yesterday = now.subtract(1, 'day').format('MM/DD/YYYY');
        const msgDate = msgSentAt.format('MM/DD/YYYY');
        const msgTime = msgSentAt.format('h:mm a');

        if (msgDate === today) {
            return `Today @ ${msgTime}`;
        }

        if (msgDate === yesterday) {
            return `Yesterday @ ${msgTime}`;
        }

        return `${msgDate} @ ${msgTime}`;
    };

    const handlePressMessage = (stashboardMessage: Stashboard) => {

        navigate(`w/${walletId || params.wallet_id}/messages/${stashboardMessage._key}`, {
            state: {
                message: stashboardMessage
            },
        });
    };

    useCallback(() => {

        if (data) {

            setStashboardMedia(handleMms(data?.stashboard_media));
        }
    }, [data]);

    return (
        <Center
            flex={1}
            w={'100%'}
            position='relative'
            flexDirection={'column'}
            justifyContent={'flex-start'}
        >
            <H3 py={4}>
                { handleDate(data?.sent_at) }
            </H3>
            {data.content_type === 'mp5' ? (
                <VisibilitySensor
                    onChange={setIsVisible}
                    partialVisibility={true}
                    minTopValue={1}
                >
                    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
                        {isVisible && (
                            <iframe
                                src={data.mp5_url}
                                style={{
                                    width: '100%',
                                    height: '100vh',
                                    border: 'none',
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                                allowFullScreen
                            />
                        )}
                        {!isVisible && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100vh',
                                    zIndex: 2,
                                    backgroundColor: 'white',
                                }}
                            >
                                <Skeleton width='100%' height='100vh' />
                            </div>
                        )}
                    </div>
                </VisibilitySensor>
            ) : (
                <Carousel images={stashboardMedia} stashboard={data} />
            )}
            {
                hasMoreContent ?
                    <Box w={'100%'}>
                        <Box w='100%'>
                            <Button
                                onPress={() => handlePressMessage(data)}
                                fontWeight={'bold'}
                                modifier='unlocked'
                                backgroundColor={'transparent'} 
                                borderColor={'transparent'} 
                            >
                                <Caption w={'100%'}
                                    textAlign='center'
                                    fontSize={14}
                                >
                                    { data?.sms_content && data?.sms_content.slice(0, 220) }
                                    {
                                        data?.sms_content && data?.sms_content.length > 220 && (
                                            <Caption
                                                color={highlight_text}
                                                fontWeight={'bold'}
                                            >
                                                {' '}...See More
                                            </Caption>
                                        )
                                    }
                                </Caption>
                            </Button>
                            { !data?.full_image_content &&
                                <SliderLinkCard data={data} />
                            }
                        </Box>
                    </Box>
                    :            
                    <Box w='100%' px={1}>
                        <Button
                            onPress={() => handlePressMessage(data)}
                            modifier='unlocked'
                            bg={'transparent'} 
                            borderColor={'transparent'} 
                        >
                            <Caption
                                w={'100%'}
                                textAlign='center'
                                fontSize={14}
                            >
                                { data?.sms_content || '' }
                            </Caption>
                        </Button>
                        { !data?.full_image_content &&
                            <SliderLinkCard data={data} />
                        }
                    </Box>
            }
        </Center>
    );
};

export default MessageCarousel;
